import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Filter, Repeater, Featured, SearchBlog } from './components/blog';
import { SafeHTML, ContentLoader } from './components/common';
import { ErrorDisplay, NetworkErrorDisplay } from './components/common/ErrorBoundary';
import { useWordPressAPI } from './hooks/useWordPressAPI';
import { useTemplateEngine } from './hooks/useTemplateEngine';
import { useURLState } from './hooks/useURLState';
import { BlogConfig, SelectedFilters } from './types';

const App: React.FC<BlogConfig> = (props) => {
  // Type guards and validation for props
  const validateProps = useCallback((props: BlogConfig): BlogConfig => {
    const perPageValue = typeof props.perPage === 'string' ? props.perPage : String(props.perPage);

    return {
      ...props,
      perPage: perPageValue,
      sidebar: props.sidebar === 'true' ? 'true' : 'false',
      blogSearch: props.blogSearch === 'true' ? 'true' : 'false',
      displayCategories: props.displayCategories === 'true' ? 'true' : 'false',
      displayTags: props.displayTags === 'true' ? 'true' : 'false',
    };
  }, []);

  const validatedProps = useMemo(() => validateProps(props), [props, validateProps]);

  // Initialize services
  const { urlState, setPage, setSearchQuery, setActiveFilters, scrollToTop } = useURLState();
  const templateEngine = useTemplateEngine();

  // Refs for DOM elements
  const blogFormRef = useRef<HTMLDivElement>(null);
  const blogElementRef = useRef<HTMLDivElement>(null);

  // WordPress API integration
  const {
    posts,
    featuredPosts,
    tags,
    totalPages,
    loading,
    loadingFeatured,
    loadingTags,
    error,
    loadPosts,
    loadFeaturedPosts,
    retry,
  } = useWordPressAPI({
    baseUrl: validatedProps.url,
    autoLoad: true,
    defaultParams: {
      per_page: Number(validatedProps.perPage),
      _embed: true,
    },
  });

  // Component state
  const [selectedTags, setSelectedTags] = useState<SelectedFilters>({});
  const [displayTags, setDisplayTags] = useState(true);
  const [showTop, setShowTop] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const setSelectedTagsCallback = useCallback((categories: SelectedFilters) => {
    setSelectedTags(categories);
  }, []);

  useEffect(() => {
    if (validatedProps.url.includes('tags')) {
      setDisplayTags(false);
    }
  }, [validatedProps.url]);

  // Load posts when URL state changes
  useEffect(() => {
    const params = {
      per_page: Number(validatedProps.perPage),
      page: urlState.page ?? 1,
      _embed: true,
      ...(urlState.search && { search: urlState.search }),
      ...(urlState.filters && urlState.filters.length > 0 && { tags: urlState.filters.join(',') }),
    };

    loadPosts(params);
  }, [loadPosts, validatedProps.perPage, urlState.page, urlState.search, urlState.filters]);

  // Load featured posts
  useEffect(() => {
    loadFeaturedPosts();
  }, [loadFeaturedPosts]);

  // Track initial load completion
  useEffect(() => {
    if (isInitialLoad && !loading && !loadingFeatured && !loadingTags) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, loading, loadingFeatured, loadingTags]);

  // Combined loading state for initial page load
  const isInitiallyLoading = isInitialLoad && (loading || loadingFeatured || loadingTags);

  const toggleTags = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedTags = { ...selectedTags, [Number(event.target.value)]: event.target.checked };
      setSelectedTags(updatedTags);
      const activeFilters = Object.entries(updatedTags)
        .filter(([, value]) => value)
        .map(([key]) => Number(key));

      setActiveFilters(activeFilters);
      setPage(1);
    },
    [selectedTags, setActiveFilters, setPage]
  );

  const handleChangePage = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setShowTop(true);
    },
    [setPage]
  );

  const searchInput = useCallback(
    (inputVal: string) => {
      setSearchQuery(inputVal);
      setPage(1);
    },
    [setSearchQuery, setPage]
  );

  const buildEngageForm = useCallback((): string => {
    if (blogFormRef.current && blogFormRef.current.innerHTML !== '') {
      const formHTML = blogFormRef.current.innerHTML;
      blogFormRef.current.style.display = 'none';
      return formHTML;
    } else {
      return '<b> Missing formstack form or error during loading formstack form. </b>';
    }
  }, []);

  const setParamTags = useCallback(() => {}, []);

  useEffect(() => {
    if (showTop) {
      if ((urlState.page ?? 1) === 1) {
        scrollToTop();
      } else {
        scrollToTop(validatedProps.blogElementIndexId);
      }
      setShowTop(false);
    }
  }, [validatedProps.blogElementIndexId, showTop, urlState.page, scrollToTop]);

  // Process posts and featured posts with template engine - memoized for performance
  const htmlPosts = useMemo(
    () => templateEngine.processMultipleItems(validatedProps.markup, posts),
    [templateEngine, validatedProps.markup, posts]
  );

  const featuredHtmlPosts = useMemo(
    () =>
      featuredPosts.map((post) =>
        templateEngine.processTemplate(validatedProps.featuredMarkup ?? '', post)
      ),
    [templateEngine, validatedProps.featuredMarkup, featuredPosts]
  );

  // Enhanced error handling with user-friendly messages and retry functionality
  if (error) {
    const isNetworkError =
      error.message.toLowerCase().includes('network') ||
      error.message.toLowerCase().includes('fetch') ||
      error.message.toLowerCase().includes('connection');

    if (isNetworkError) {
      return <NetworkErrorDisplay onRetry={retry} />;
    }

    return (
      <ErrorDisplay
        error={{ message: error.message, type: 'API Error' }}
        onRetry={retry}
        onDismiss={() => window.location.reload()}
      />
    );
  }

  // Show loading spinner during initial page load until all components are ready
  if (isInitiallyLoading) {
    return <ContentLoader />;
  }

  return (
    <>
      {/* Hidden form scripts container */}
      <div id="blogFormScripts" ref={blogFormRef} style={{ display: 'none' }} />

      <div
        id={validatedProps.blogElementIndexId}
        ref={blogElementRef}
        className="row blog-index-page"
      >
        {validatedProps.blogSearch === 'true' && (
          <SearchBlog
            queryValue={urlState.search ?? ''}
            setQueryValue={(value) => setSearchQuery(value)}
            searchInput={searchInput}
          />
        )}

        <div className="blog-index-main">
          <Repeater
            setShowTop={setShowTop}
            posts={posts}
            htmlPosts={htmlPosts}
            setPage={(page) => setPage(page)}
            page={urlState.page ?? 1}
            totalPages={totalPages}
            loading={loading && !isInitialLoad}
            handleChangePage={handleChangePage}
          />
        </div>

        {validatedProps.sidebar !== 'false' && (
          <div className="d-none d-lg-block blog-index-sidebar">
            {featuredHtmlPosts.length > 0 && (
              <Featured posts={featuredHtmlPosts} name={validatedProps.featuredTitle} />
            )}
            {validatedProps.formToken &&
              validatedProps.formToken !== '' &&
              validatedProps.formToken !== 'null' && (
                <SafeHTML className="blog-index-form" html={buildEngageForm()} />
              )}
            {displayTags && (
              <Filter
                filters={tags}
                name="Filters"
                selectedCategories={selectedTags}
                setSelectedCategories={setSelectedTagsCallback}
                toggleFilter={toggleTags}
                setParamTags={setParamTags}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default App;
